import React, { useState, useEffect } from 'react';
import { Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { InputGroup } from '../common/InputGroup';
import { Review } from '../../types/review';

import { useProfileStore } from '../../stores/profileStore';
import { useReviewExistence } from '../../hooks/useReviewExists';

interface ReviewFormProps {
  quoteId: string;
  username: string;
  reviewedId: string;
  onChange: (data: Review) => void;
}

export const ReviewForm: React.FC<ReviewFormProps> = ({
  quoteId,
  username,
  reviewedId,
  onChange,
}) => {
  const { profile } = useProfileStore();
  const reviewExists = useReviewExistence(quoteId, reviewedId);
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const { t } = useTranslation(['reviews', 'common']);

  useEffect(() => {
    if (profile && rating !== null) {
      onChange({
        quote_id: quoteId,
        reviewed_id: reviewedId,
        reviewer_id: profile.id,
        rating,
        comment,
      });
    }
  }, [rating, comment, profile, onChange, quoteId, reviewedId]);

  if (reviewExists) {
    return (
      <div className="bg-yellow-50 border border-yellow-400 text-yellow-700 px-4 py-3 my-4 rounded">
        {t('reviewAlreadySubmitted', {
          defaultValue: 'Vous avez déjà évalué {{name}} dans ce projet.',
          name: username || 'cet utilisateur',
        })}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="space-y-4">
        <div className="flex space-x-1">
          {[1, 2, 3, 4, 5].map(value => (
            <button
              key={value}
              type="button"
              onClick={() => setRating(value)}
              className="focus:outline-none"
            >
              <Star
                className={`h-6 w-6 ${
                  value <= (rating || 0) ? 'text-primary fill-current' : 'text-primary'
                }`}
              />
            </button>
          ))}
        </div>

        <InputGroup
          label={t('comment', { ns: 'reviews', defaultValue: 'Commentaire' })}
          as="textarea"
          value={comment}
          onChange={e => setComment(e.target.value)}
          placeholder={t('commentPlaceholder', {
            ns: 'reviews',
            defaultValue: 'Laisser un commentaire...',
          })}
        />
      </div>
    </div>
  );
};
